import { apiAuthHeaders } from '../../core/api.js';
import { openPhotoModalHero } from '../../app/photo-modal.js';

export interface OwnerChatPhotoBlock {
  wrap: HTMLDivElement;
  img: HTMLImageElement;
  setProgress: (pct: number, label?: string) => void;
  reveal: (src: string) => void;
  fail: () => void;
}

function finishOwnerChatPhotoReveal(wrap: HTMLDivElement, img: HTMLImageElement) {
  wrap.classList.remove('chat-bubble-image--loading', 'chat-bubble-image--failed');
  const progress = wrap.querySelector('.chat-bubble-image-progress');
  if (progress) progress.remove();
  img.hidden = false;
  img.style.opacity = '';
  img.style.visibility = '';
}

function revealOwnerChatPhoto(
  wrap: HTMLDivElement,
  img: HTMLImageElement,
  label: HTMLSpanElement | null,
  fill: HTMLDivElement | null,
  src: string,
  onFail: () => void
) {
  const raw = String(src || '');
  if (!raw) {
    onFail();
    return;
  }

  if (fill) fill.style.width = '100%';
  if (label) label.textContent = '100%';

  let done = false;
  const finish = function () {
    if (done) return;
    done = true;
    finishOwnerChatPhotoReveal(wrap, img);
  };

  img.loading = 'eager';
  img.removeAttribute('hidden');
  img.style.opacity = '0';
  img.style.visibility = 'visible';
  img.onload = finish;
  img.onerror = function () {
    if (done) return;
    done = true;
    onFail();
  };
  img.src = raw;

  if (typeof img.decode === 'function') {
    void img.decode().then(finish).catch(function () {
      if (!done && img.complete && img.naturalWidth > 0) finish();
    });
  } else if (img.complete && img.naturalWidth > 0) {
    finish();
  }

  window.setTimeout(function () {
    if (!done && img.complete && img.naturalWidth > 0) finish();
  }, 1200);
}

export function createOwnerChatPhotoBlock(): OwnerChatPhotoBlock {
  const wrap = document.createElement('div');
  wrap.className = 'chat-bubble-image chat-bubble-image--loading';

  const progress = document.createElement('div');
  progress.className = 'chat-bubble-image-progress';
  progress.setAttribute('aria-hidden', 'false');

  const track = document.createElement('div');
  track.className = 'chat-bubble-image-progress-track';

  const fill = document.createElement('div');
  fill.className = 'chat-bubble-image-progress-fill';

  const label = document.createElement('span');
  label.className = 'chat-bubble-image-progress-label';
  label.textContent = '0%';

  track.appendChild(fill);
  progress.appendChild(track);
  progress.appendChild(label);
  wrap.appendChild(progress);

  const img = document.createElement('img');
  img.loading = 'eager';
  img.alt = '';
  img.hidden = true;
  img.addEventListener('click', function () {
    try {
      openPhotoModalHero(img);
    } catch (_) {}
  });
  wrap.appendChild(img);

  function setProgress(pct: number, labelText?: string) {
    const p = Math.max(0, Math.min(100, Math.round(pct)));
    fill.style.width = p + '%';
    label.textContent = labelText != null ? labelText : p + '%';
  }

  function reveal(src: string) {
    revealOwnerChatPhoto(wrap, img, label, fill, src, fail);
  }

  function fail() {
    wrap.classList.add('chat-bubble-image--failed');
    setProgress(0, 'Ошибка');
  }

  return { wrap, img, setProgress, reveal, fail };
}

export function ownerChatPhotoProgressByMsgId(msgId: number | string): OwnerChatPhotoBlock | null {
  const row = document.querySelector('.chat-msg-row[data-msg-id="' + String(msgId) + '"]');
  if (!row) return null;
  const wrap = row.querySelector('.chat-bubble-image');
  if (!(wrap instanceof HTMLDivElement)) return null;
  const img = wrap.querySelector('img');
  const fill = wrap.querySelector('.chat-bubble-image-progress-fill');
  const label = wrap.querySelector('.chat-bubble-image-progress-label');
  if (!(img instanceof HTMLImageElement) || !(fill instanceof HTMLDivElement) || !(label instanceof HTMLSpanElement)) {
    return null;
  }

  return {
    wrap,
    img,
    setProgress(pct: number, labelText?: string) {
      const p = Math.max(0, Math.min(100, Math.round(pct)));
      fill.style.width = p + '%';
      label.textContent = labelText != null ? labelText : p + '%';
    },
    reveal(src: string) {
      revealOwnerChatPhoto(wrap, img, label, fill, src, function () {
        wrap.classList.add('chat-bubble-image--failed');
        label.textContent = 'Ошибка';
      });
    },
    fail() {
      wrap.classList.add('chat-bubble-image--failed');
      label.textContent = 'Ошибка';
    }
  };
}

export function xhrAuthedJson(
  url: string,
  body: string,
  onProgress?: (pct: number) => void
): Promise<{ ok: boolean; status: number; data: unknown }> {
  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    const headers = apiAuthHeaders({ 'Content-Type': 'application/json' });
    Object.keys(headers).forEach(function (key) {
      xhr.setRequestHeader(key, headers[key]);
    });
    xhr.upload.onprogress = function (ev) {
      if (!onProgress || !ev.lengthComputable || ev.total <= 0) return;
      onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = function () {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = function () {
      reject(new Error('network_error'));
    };
    xhr.send(body);
  });
}

export function xhrAuthedBlob(
  url: string,
  onProgress?: (pct: number) => void
): Promise<{ ok: boolean; blobUrl: string }> {
  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    const headers = apiAuthHeaders();
    Object.keys(headers).forEach(function (key) {
      xhr.setRequestHeader(key, headers[key]);
    });
    xhr.onprogress = function (ev) {
      if (!onProgress) return;
      if (!ev.lengthComputable || ev.total <= 0) {
        onProgress(50);
        return;
      }
      onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = function () {
      if (xhr.status < 200 || xhr.status >= 300 || !xhr.response) {
        resolve({ ok: false, blobUrl: '' });
        return;
      }
      resolve({ ok: true, blobUrl: URL.createObjectURL(xhr.response) });
    };
    xhr.onerror = function () {
      reject(new Error('network_error'));
    };
    xhr.send();
  });
}
